import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem } from '@mui/material';
// routes
import { PATH_DASHBOARD, PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { adminRoles, decrypt } from 'src/helpers/constants';
import { SuccessModal } from 'src/pages/Client/App/AddEvent/SuccessModal';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Edit Profile',
    accessLevels: [1, 2, 3, 4, 5],
    linkTo: '/admin/edit-profile',
  },
  {
    label: 'Manage Admins',
    accessLevels: [1],
    linkTo: PATH_DASHBOARD.user.administrators,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout } = useAuth();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);

  const [showDetails, setShowDetails] = useState(false);

  const event = localStorage.getItem('arsh:activeevent');

  const roleInformation =
    adminRoles.find((item) => item.title?.toLowerCase() === user?.role?.toLowerCase()) || {
      id: 999,
      title: user?.role || 'Restricted',
    };

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const logoutToClient = async () => {
    try {
      await logout();
      navigate('/client/login', { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {decrypt(user?.name)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {decrypt(user?.email) || decrypt(user?.username)}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map(
            (option) =>
              option?.accessLevels?.includes(roleInformation?.id) && (
                <MenuItem key={option.label} to={option.linkTo} component={RouterLink} onClick={handleClose}>
                  {option.label}
                </MenuItem>
              )
          )}
          <MenuItem component={RouterLink} to="" onClick={() => setShowDetails(true)}>
            Show Event Links
          </MenuItem>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
        {roleInformation?.id === 1 && (
          <MenuItem onClick={logoutToClient} sx={{ m: 1 }}>
            Login to Client Account
          </MenuItem>
        )}
      </MenuPopover>
      {showDetails && <SuccessModal event={event} onClose={() => setShowDetails(false)} />}
    </>
  );
}
